import React, { useEffect, useMemo, useRef, useState } from "react";
import moment from "moment";
import { Link, useLocation } from "react-router-dom";
import SiteHeader from "./SiteHeader";
import SiteFooter from "./SiteFooter";
import { ResultCard, TodayCard, UpcomingCard } from "./SiteCards";
import { MONTHS, cardMatches, groupSeasons, lastTwelveMonths } from "./siteData";
import { useHashScroll, usePageMeta, useSiteMotion } from "./useSiteMotion";
import { loadListings } from "../Components/EventFeed/EventFeed";
import { buildEventFeed } from "../helper/eventFeed";
import founder from "../Assets/founder-dancers.jpg";
import "./site.css";

// The signed-out home page, for families: what's open, then results, then who
// makes Feis.link. Organiser material lives on /organisers (Devin
// 2026-10-15). With nothing happening or coming up, the events area is left
// out rather than showing an off-season message.

const HEADLINE = [["Feis", ""], ["software,", ""], ["finally", "g"], ["modern", "g"]];

// Results default to the last 12 months; the season buttons show one whole
// season; a search covers every season.
const RECENT = "recent";

const Results = ({ seasons, now }) => {
  const [view, setView] = useState(RECENT);
  const [query, setQuery] = useState("");
  const body = useRef(null);
  const [reached, setReached] = useState({ p: 0, months: new Set() });
  const q = query.trim();
  const recent = useMemo(() => lastTwelveMonths(seasons, now), [seasons, now]);
  // With nothing in the last 12 months, open on the newest season instead.
  const current = view === RECENT && !recent.length ? seasons[0]?.year : view;

  const months = useMemo(() => {
    const all = seasons.flatMap((s) => s.months);
    if (q) return all.map((m) => ({ ...m, cards: m.cards.filter((c) => cardMatches(c, q)) })).filter((m) => m.cards.length);
    if (current === RECENT) return recent;
    return all.filter((m) => m.year === current);
  }, [seasons, recent, current, q]);
  const hits = q ? months.reduce((n, m) => n + m.cards.length, 0) : 0;
  const showYear = !!q || current === RECENT;

  // The timeline rail fills and each month's dot lights as it scrolls past.
  useEffect(() => {
    if (q) return undefined;
    let frame = 0;
    const update = () => {
      frame = 0;
      const el = body.current;
      if (!el) return;
      const vh = window.innerHeight;
      const r = el.getBoundingClientRect();
      const p = Math.max(0, Math.min(1, (vh * 0.55 - r.top) / r.height));
      const months = new Set();
      el.querySelectorAll(".month").forEach((m) => { if (m.getBoundingClientRect().top < vh * 0.55) months.add(m.dataset.m); });
      setReached({ p, months });
    };
    const onScroll = () => { if (!frame) frame = requestAnimationFrame(update); };
    update();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
      if (frame) cancelAnimationFrame(frame);
    };
  }, [q, current]);

  return (
    <section className={`season wrap${q ? " searching" : ""}`} id="season" aria-labelledby="season-h">
      <div className="sec-head rv">
        <div>
          <h2 id="season-h">Results</h2>
          <p>{q ? "Results across every season, newest first." : current === RECENT ? "The last 12 months, newest first." : <>The <span className="yr">{current}</span> season, newest first.</>}</p>
        </div>
        <div className="years" role="group" aria-label="Season">
          {recent.length > 0 && (
            <button type="button" aria-pressed={!q && current === RECENT} onClick={() => { setQuery(""); setView(RECENT); }}>
              Last 12 months
            </button>
          )}
          {seasons.map((s) => (
            <button
              key={s.year}
              type="button"
              aria-pressed={!q && s.year === current}
              onClick={() => { setQuery(""); setView(s.year); }}
            >
              {s.year}
            </button>
          ))}
          <Link to="/results">All results ›</Link>
        </div>
      </div>
      <form className="rsearch rv" role="search" onSubmit={(e) => e.preventDefault()}>
        <svg aria-hidden="true" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><circle cx="11" cy="11" r="7" /><path d="m20 20-3.5-3.5" /></svg>
        <label htmlFor="rq" className="sr">Search all results by feis, city or year</label>
        <input id="rq" type="search" placeholder="Search all results by feis, city or year" autoComplete="off" value={query} onChange={(e) => setQuery(e.target.value)} />
        <span className="rcount" aria-live="polite">{q ? `${hits} ${hits === 1 ? "feis" : "feiseanna"}` : ""}</span>
        {query && <button className="rclear" type="button" aria-label="Clear search" onClick={() => setQuery("")}>×</button>}
      </form>
      {q && hits === 0 && <p className="rempty">No results match that search. Try a feis name, a city or a year.</p>}
      {months.length > 0 && (
        <div className={`season-body${showYear ? " showyear" : ""}`} ref={q ? undefined : body}>
          {!q && <div className="rail" aria-hidden="true"><i style={{ "--p": reached.p }} /></div>}
          {months.map((m) => (
            <div className={`month${reached.months.has(`${m.year}-${m.month}`) ? " reached" : ""}`} data-m={`${m.year}-${m.month}`} key={`${m.year}-${m.month}`}>
              <h3><span>{MONTHS[m.month]}<em className="y"> {m.year}</em></span></h3>
              <div className="sc-grid">
                {m.cards.map((c, i) => <ResultCard card={c} index={i} key={c.key} />)}
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
};

const Home = () => {
  const root = useRef(null);
  const location = useLocation();
  const [feed, setFeed] = useState(null);
  const [now] = useState(() => new Date());

  usePageMeta(
    "Feis.link: Feis Registration, Tabulation & Results",
    "Register for Irish dance feiseanna, pay online and get results by email the moment they're published. Running feiseanna since 2019.",
  );

  useEffect(() => {
    let cancelled = false;
    // Same "now" as the app's feed: local wall-clock time expressed as UTC.
    loadListings(moment(new Date()).utc(true).format())
      .catch(() => ({}))
      .then((buckets) => { if (!cancelled) setFeed(buildEventFeed(buckets, new Date())); });
    return () => { cancelled = true; };
  }, []);

  const seasons = useMemo(() => groupSeasons(feed?.past || []), [feed]);
  const animate = useSiteMotion(root);
  useHashScroll(location, !!feed);

  const today = feed?.today || [];
  // Arrived from /delete-account after the account was deleted.
  const accountDeleted = !!location.state?.accountDeleted;
  const upcoming = feed?.future || [];

  return (
    <div className={`site${animate ? " js" : ""}`} ref={root}>
      <SiteHeader active="events" />
      <main>
        <section className="hero" id="events">
          <div className="wrap">
            {accountDeleted && <p className="notice" role="status">Your account has been deleted.</p>}
            <div className="hero-head">
              <h1>
                {HEADLINE.map(([w, c], i) => (
                  <React.Fragment key={w}>
                    {i > 0 && " "}
                    <span className={`w${c ? ` ${c}` : ""}`} style={{ "--i": i }}>{w}</span>
                  </React.Fragment>
                ))}
                <span className="w a" style={{ "--i": HEADLINE.length }}>.</span>
              </h1>
              <p className="lede rv">Register in a few minutes. Get your results the moment they're published.</p>
              <div className="hero-acts rv">
                <Link className="sbtn big" to="/signup">Sign up</Link>
                <Link className="sbtn big ghost" to="/login">Log in</Link>
              </div>
            </div>
            {(today.length > 0 || upcoming.length > 0) && (
              <div className="events" aria-live="polite">
                {today.length > 0 && (
                  <>
                    <div className="ev-h"><p className="now-h live"><i />Happening now</p></div>
                    <div className="ev-grid">{today.map((item) => <TodayCard item={item} key={item.event._id} />)}</div>
                  </>
                )}
                {upcoming.length > 0 && (
                  <>
                    <div className="ev-h" style={today.length ? { marginTop: 32 } : undefined}><h2>Upcoming events</h2></div>
                    <div className="ev-grid">{upcoming.map((item) => <UpcomingCard item={item} now={now} key={item.event._id} />)}</div>
                  </>
                )}
              </div>
            )}
          </div>
        </section>

        {seasons.length > 0 && <Results seasons={seasons} now={now} />}

        {/* Held back until the events arrive, so the photo doesn't show
            under the headline and then get pushed down the page. */}
        {feed && (
          <section className="join" id="made" aria-labelledby="made-h">
            <div className="wrap">
              <div className="founder rv"><img src={founder} alt="Devin Cornell with his dancers in team costumes" loading="lazy" width="1600" height="762" /></div>
              <div className="panel made-panel rv">
                <h2 id="made-h">Made at the feis</h2>
                <p>Feis.link was built by Devin Cornell, who has seen the feis from every side: as a dance teacher, an adjudicator, a feis organiser for ten years and a dance parent.</p>
                <p>Have questions? <a className="inline" href="mailto:info@feis.link">info@feis.link</a></p>
                <p className="who"><b>Devin Cornell, ADCRG</b> · Founder · <Link className="inline" to="/about">About Feis.link</Link></p>
              </div>
            </div>
          </section>
        )}
      </main>
      {feed && <SiteFooter />}
    </div>
  );
};

export default Home;
